import { tvScreenGet, TvApiError } from "../../../hooks/useTvCatalogue";
import type { StreamFailure } from "../streamPlayback";
import type { StreamLicence } from "../StreamSurface";

/**
 * Turning a press into something {@link StreamSurface} can play (`TV-3`).
 *
 * Deliberately thin, and deliberately NOT a second player: `StreamSurface` and
 * `streamPlayback.ts` are the shipped core and this file neither reimplements
 * nor edits them. All it does is ask the server what to play and translate a
 * refusal into a named {@link StreamFailure}, because the screen must be able to
 * say what went wrong BEFORE a surface exists to say it — a resolve that fails
 * has no `<video>` element to paint the message on.
 *
 * ## The refusal vocabulary is upstream's, and it is not widened here
 *
 * `playbackResolver.resolvePlayback` answers
 * `{playable: {url, encrypted, licenceUrl}}` or `{refusal: {code}}`, and the
 * entitlement gate answers `403 forbidden`. Those codes are facts about rights
 * and about the catalogue; `StreamFailure` is the vocabulary the SCREEN and the
 * fleet console share. {@link failureForRefusal} is the one place the two meet,
 * and it maps conservatively: anything it cannot place becomes `unknown` rather
 * than being forced into a neighbouring bucket, for the reason
 * `classifyStreamError` gives — a wrong name sends someone to fix the wrong
 * thing.
 *
 * ## Why there is no renewal here either
 *
 * {@link mintLicenceFor} re-resolves on every licence request the CDM makes, so
 * the token in flight is never older than one round trip. That is
 * `streamPlayback.ts`'s design, used rather than duplicated: a two-hour film, a
 * key rotation and a resumed session are the same case and none of them is
 * special.
 */

/** What a press resolved to. */
export interface TvPlayback {
  /** The manifest, for `StreamSurface`'s `url`. */
  url: string;
  /** Whether a licence will be needed at all. */
  encrypted: boolean;
  /** The licence to start with. Null for clear content. */
  licence: StreamLicence | null;
}

/**
 * A resolve that did not produce something playable.
 *
 * An error class rather than a union return because every caller of
 * {@link resolveTvPlayback} is inside an `await` that already has a `catch`, and
 * a second success/failure shape at that seam is how the network case ends up
 * being handled and the refusal case silently is not.
 */
export class TvPlaybackRefused extends Error {
  constructor(
    readonly failure: StreamFailure,
    /** The server's own code, for diagnostics. Never shown to a guest. */
    readonly code: string
  ) {
    super(`playback refused: ${code}`);
    this.name = "TvPlaybackRefused";
  }
}

/**
 * How long a resolve may take before it is called unreachable.
 *
 * ⚠️ There is a timeout at all because the alternative is the exact failure this
 * whole player was built to remove: a guest presses Play, the request hangs on a
 * captive portal that never answers, and the television shows a spinner until
 * somebody unplugs it. Eight seconds is long enough for a slow hotel uplink and
 * short enough that a guest presses Back rather than reception.
 */
export const RESOLVE_TIMEOUT_MS = 8_000;

/** Upstream refusal codes, mapped onto the screen's vocabulary. */
export function failureForRefusal(code: string): StreamFailure {
  switch (code) {
    // Rights, not plumbing. The guest reads "not available on this television",
    // which is precisely true and does not blame the connection.
    case "forbidden":
    case "not_entitled":
      return "licence_refused";
    // A licence could not be minted at all — the key material or the signing
    // service, not this room's entitlement. Still a licence problem, and the
    // fleet console needs it counted as one.
    case "no_licence_available":
      return "licence_expired";
    // The catalogue has nothing playable behind this row. None of the five
    // failure names fits a catalogue gap, and inventing a sixth here would put
    // a word in the fleet console's vocabulary that no other producer emits.
    // `unknown` is the honest bucket; the raw code rides on the error.
    //
    // `no_licence_server` joins them although it is an ENVIRONMENT with nothing
    // configured rather than a catalogue gap: it is still not one of the five
    // names the fleet console speaks, and the raw code riding on the error is
    // what keeps the distinction recoverable from a capture.
    case "no_playable_media":
    case "asset_has_no_media":
    case "catchup_asset_has_no_epg_events":
    case "encrypted_without_dash":
    case "no_drm_content_id":
    case "drm_content_id_unverified":
    case "no_licence_server":
      return "unknown";
    default:
      return "unknown";
  }
}

function refusalCode(body: unknown): string | null {
  if (!body || typeof body !== "object") return null;
  const o = body as Record<string, unknown>;
  const refusal = o.refusal;
  if (refusal && typeof refusal === "object") {
    const code = (refusal as Record<string, unknown>).code;
    if (typeof code === "string" && code) return code;
    return "unknown";
  }
  if (typeof o.refusal === "string") return o.refusal;
  if (typeof o.error === "string") return o.error;
  if (typeof o.reason === "string") return o.reason;
  return null;
}

/**
 * Ask what to play, once.
 *
 * Throws {@link TvPlaybackRefused} for every non-playable outcome — a refusal, a
 * 403, a timeout, a body that does not contain a manifest — so a caller has one
 * failure path rather than four.
 */
export async function resolveTvPlayback(
  assetId: string,
  timeoutMs = RESOLVE_TIMEOUT_MS
): Promise<TvPlayback> {
  const controller =
    typeof AbortController === "function" ? new AbortController() : null;
  const timer = setTimeout(() => controller?.abort(), timeoutMs);
  let body: unknown;
  try {
    body = await tvScreenGet(`play/${encodeURIComponent(assetId)}`, {
      signal: controller?.signal,
    });
  } catch (e) {
    // 401/403 is the entitlement gate speaking, and it is the one HTTP status
    // that means something specific to a guest. Everything else — a 500, a
    // timeout, a captive portal — is delivery.
    if (e instanceof TvApiError) {
      if (e.status === 401 || e.status === 403) {
        throw new TvPlaybackRefused("licence_refused", `http_${e.status}`);
      }
      throw new TvPlaybackRefused("manifest_unreachable", `http_${e.status}`);
    }
    throw new TvPlaybackRefused("manifest_unreachable", "unreachable");
  } finally {
    clearTimeout(timer);
  }

  const code = refusalCode(body);
  if (code) throw new TvPlaybackRefused(failureForRefusal(code), code);

  const playback = readPlayable(body);
  // A 200 with no manifest in it is a proxy or a captive portal answering for
  // the API, not a title with no stream. Named rather than played as silence.
  if (!playback) throw new TvPlaybackRefused("manifest_unreachable", "no_url");
  return playback;
}

function readPlayable(body: unknown): TvPlayback | null {
  if (!body || typeof body !== "object") return null;
  const outer = body as Record<string, unknown>;
  const o =
    outer.playable && typeof outer.playable === "object"
      ? (outer.playable as Record<string, unknown>)
      : outer;
  const url = pick(o, "url", "manifestUrl");
  if (!url) return null;
  const widevineUrl = pick(o, "licenceUrl", "licenseUrl", "widevineUrl");
  const playreadyUrl = pick(o, "playreadyUrl");
  const encrypted =
    typeof o.encrypted === "boolean"
      ? o.encrypted
      : !!(widevineUrl || playreadyUrl);
  return {
    url,
    encrypted,
    licence: widevineUrl || playreadyUrl ? { widevineUrl, playreadyUrl } : null,
  };
}

function pick(
  o: Record<string, unknown>,
  ...keys: string[]
): string | undefined {
  for (const k of keys) {
    const v = o[k];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return undefined;
}

/**
 * A `mintLicence` for `StreamSurface`, bound to one asset.
 *
 * Returns `null` rather than throwing when a fresh token cannot be had, because
 * that is what the surface's request filter is built to survive: it leaves the
 * request alone, Shaka fails it, and the classifier calls it a refusal — which
 * is the truth. Throwing here would surface as an unrecognisable internal error
 * instead.
 */
export function mintLicenceFor(
  assetId: string
): () => Promise<StreamLicence | null> {
  return async () => {
    try {
      const fresh = await resolveTvPlayback(assetId);
      return fresh.licence;
    } catch {
      return null;
    }
  };
}
